import React, { useState, useEffect } from "react";
import axios from "axios";
import { submit_vip_FormData, updateVip } from "../api/vipform";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function VIPForm() {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    email: "",
    gender: "",
    adharNo: "",
    username: "",
    password: "",
    designation: "",
    contactno: "",
    picture: "",
    status: "Inactive",
  });

  const [designations, setDesignations] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [otherDesignation, setOtherDesignation] = useState("");
  const [showOtherDesignation, setShowOtherDesignation] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [contactError, setContactError] = useState("");
  const [refers, setRefers] = useState("false");
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  /* FETCH DESIGNATIONS */
  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const role = localStorage.getItem("role");
        const token = localStorage.getItem(`${role}Token`);

        const response = await axios.get(
          `${BASE_URL}/api/categories/unique-designation`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setDesignations(response.data || []);
      } catch (error) {
        console.error("Failed to fetch designations:", error);
        toast.error("Unable to load designations!");
        setDesignations([]);
      }
    };

    fetchDesignations();
  }, [refers]);

  /* INPUT HANDLER */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      setFormData((prev) => ({ ...prev, email: value }));
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(emailRegex.test(value) || value === "" ? "" : "Invalid email format");
      return;
    }

    if (name === "contactno") {
      if (/^\d{0,10}$/.test(value)) {
        setFormData((prev) => ({ ...prev, contactno: value }));
        setContactError(value.length === 10 || value === "" ? "" : "Must be exactly 10 digits");
      }
      return;
    }

    if (name === "adharNo") {
      if (/^\d{0,12}$/.test(value)) {
        setFormData((p) => ({ ...p, adharNo: value }));
      }
      return;
    }

    if (name === "designation") {
      setFormData((prev) => ({ ...prev, designation: value }));
      setShowOtherDesignation(value === "Other");
      if (value !== "Other") setOtherDesignation("");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImage = async (vipId) => {
    if (!imageFile) return;

    const token = localStorage.getItem("adminToken");
    const formDataImg = new FormData();
    formDataImg.append("imaged", imageFile);

    await axios.post(
      `${BASE_URL}/api/profile/upload/${vipId}/vip`,
      formDataImg,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailError || contactError) {
      toast.error("Please fix all errors!");
      return;
    }

    if (!imageFile && !isEditMode) {
      toast.error("Please select a profile picture!");
      return;
    }

    const finalData = {
      ...formData,
      designation: formData.designation === "Other" ? otherDesignation : formData.designation,
      status: "Inactive",
    };

    try {
      setLoading(true);

      if (isEditMode) {
        await updateVip(formData.id, finalData);
        toast.success("VIP details updated successfully!");
      } else {
        const res = await submit_vip_FormData(finalData);
        await uploadImage(res.id);
        toast.success("VIP registered successfully with profile picture!");
      }

      setRefers("true");
      resetForm();
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save data";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: "",
      name: "",
      email: "",
      gender: "",
      adharNo: "",
      username: "",
      password: "",
      designation: "",
      contactno: "",
      picture: "",
      status: "Inactive",
    });
    setOtherDesignation("");
    setShowOtherDesignation(false);
    setEmailError("");
    setContactError("");
    setIsEditMode(false);
    setImageFile(null);
  };

  return (
    <>
      <div className="vipform-wrapper">
        <div className="vipform-card">
          {/* Header */}
          <div className="card-header">
            <div className="header-content">
              <h1 className="title">
                {isEditMode ? "Edit VIP Details" : "VIP Registration"}
              </h1>
              <p className="subtitle">
                {isEditMode
                  ? "Update VIP information securely."
                  : "Register a new VIP with complete personal details."}
              </p>
            </div>
            <div className={`mode-badge ${isEditMode ? "edit" : "register"}`}>
              {isEditMode ? "Edit Mode" : "New Registration"}
            </div>
          </div>

          {/* Form Body */}
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email Address <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="vip@example.com"
                    className={emailError ? "error" : ""}
                    required
                  />
                  {emailError && <span className="error-text">{emailError}</span>}
                </div>

                <div className="form-group">
                  <label>Aadhaar Number <span className="required">*</span></label>
                  <input
                    type="text"
                    name="adharNo"
                    value={formData.adharNo}
                    onChange={handleChange}
                    placeholder="1234 5678 9012"
                    maxLength="12"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Gender <span className="required">*</span></label>
                  <select name="gender" value={formData.gender} onChange={handleChange} required>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Username <span className="required">*</span></label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Choose username"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter strong password"
                    required
                  />
                </div>

                <div className="form-group full-width">
                  <label>Designation <span className="required">*</span></label>
                  <select
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Designation</option>
                    {designations.map((d, i) => (
                      <option key={i} value={d}>{d}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </div>

                {showOtherDesignation && (
                  <div className="form-group full-width">
                    <label>Custom Designation <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter custom designation"
                      value={otherDesignation}
                      onChange={(e) => setOtherDesignation(e.target.value)}
                      required
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Contact Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    name="contactno"
                    value={formData.contactno}
                    onChange={handleChange}
                    placeholder="9876543210"
                    maxLength="10"
                    className={contactError ? "error" : ""}
                    required
                  />
                  {contactError && <span className="error-text">{contactError}</span>}
                  <small className="help-text">Must be exactly 10 digits</small>
                </div>

                <div className="form-group full-width">
                  <label>Profile Picture <span className="required">*</span></label>
                  <div className="file-input-wrapper">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      required={!isEditMode}
                    />
                    {imageFile && (
                      <span className="file-name">{imageFile.name}</span>
                    )}
                  </div>
                  {!imageFile && !isEditMode && (
                    <small className="help-text error-text">Required for new registration</small>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? "Processing..." : isEditMode ? "Update VIP" : "Register VIP"}
                </button>

                {isEditMode && (
                  <button type="button" className="cancel-button" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .vipform-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .vipform-card {
          width: 100%;
          max-width: 1000px;
          background: rgba(30, 41, 59, 0.9);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .card-header {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          padding: 40px;
          color: white;
          position: relative;
        }

        .header-content .title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color:#fff;
        }

        .header-content .subtitle {
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .mode-badge {
          position: absolute;
          right: 30px;
          bottom: -20px;
          background: #f59e0b;
          color: white;
          padding: 10px 24px;
          border-radius: 50px;
          font-weight: 600;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        }

        .mode-badge.edit {
          background: #059669;
        }

        .card-body {
          padding: 50px 40px;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 26px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-weight: 600;
          color: white;
          margin-bottom: 8px;
          font-size: 1rem;
        }

        .required {
          color: #f87171;
        }

        .form-group input,
        .form-group select {
          padding: 16px 20px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input::placeholder,
        .form-group select::placeholder {
          color: #94a3b8;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
          background: rgba(15, 23, 42, 0.8);
        }

        .form-group input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
        }

        .error-text {
          color: #fca5a5;
          font-size: 0.875rem;
          margin-top: 6px;
        }

        .help-text {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-top: 6px;
        }

        .file-input-wrapper {
          position: relative;
          padding: 16px 20px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px dashed rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          text-align: center;
          transition: all 0.3s ease;
        }

        .file-input-wrapper:hover {
          border-color: #3b82f6;
          background: rgba(15, 23, 42, 0.8);
        }

        .file-input-wrapper input[type="file"] {
          opacity: 0;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        .file-name {
          color: #60a5fa;
          font-size: 0.95rem;
          margin-top: 8px;
          display: block;
        }

        .form-actions {
          margin-top: 40px;
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .submit-button {
          flex: 1;
          min-width: 220px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border: none;
          padding: 18px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4);
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cancel-button {
          background: rgba(100, 116, 139, 0.3);
          color: #e2e8f0;
          border: 2px solid rgba(148, 163, 184, 0.5);
          padding: 18px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-button:hover {
          background: rgba(148, 163, 184, 0.2);
          transform: translateY(-3px);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .card-header {
            padding: 30px 20px;
          }

          .header-content .title {
            font-size: 1.8rem;
          }

          .mode-badge {
            position: static;
            display: inline-block;
            margin-top: 20px;
          }

          .card-body {
            padding: 30px 20px;
          }

          .form-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .form-actions {
            flex-direction: column;
          }

          .submit-button,
          .cancel-button {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .vipform-wrapper {
            padding: 10px;
          }

          .card-header {
            padding: 25px 15px;
          }

          .header-content .title {
            font-size: 1.6rem;
          }
        }
      `}</style>
    </>
  );
}

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { submit_vip_FormData, updateVip } from "../api/vipform";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function VIPForm() {
//   const [formData, setFormData] = useState({
//     id: "",
//     name: "",
//     email: "",
//     gender: "",
//     adharNo: "",
//     username: "",
//     password: "",
//     designation: "",
//     contactno: "",
//     picture: "",
//     status: "Inactive",
//   });

//   const [designations, setDesignations] = useState([]);
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [otherDesignation, setOtherDesignation] = useState("");
//   const [showOtherDesignation, setShowOtherDesignation] = useState(false);


//   const [emailError, setEmailError] = useState("");
//   const [contactError, setContactError] = useState("");
//   const [refers, setRefers] = useState("false");

//   const [loading, setLoading] = useState(false);


//   /* IMAGE FILE */
//   const [imageFile, setImageFile] = useState(null);


//   /* -------------------------------------------------------
//      FETCH DESIGNATIONS (DYNAMIC ONLY)
//   ------------------------------------------------------- */
//   useEffect(() => {
//     const fetchDesignations = async () => {
//       try {
//         const role = localStorage.getItem("role");
//         const token = localStorage.getItem(`${role}Token`);

//         const response = await axios.get(
//           `${BASE_URL}/api/categories/unique-designation`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );

//         setDesignations(response.data || []);
//       } catch (error) {
//         console.error("Failed to fetch designations:", error);
//         toast.error("Unable to load designations!");
//         setDesignations([]);
//       }
//     };

//     fetchDesignations();
//   }, [refers]);

//   /* -------------------------------------------------------
//      INPUT CHANGE HANDLER
//   ------------------------------------------------------- */
//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     // EMAIL VALIDATION
//     if (name === "email") {
//       setFormData((prev) => ({ ...prev, email: value }));
//       setTimeout(() => {
//         const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//         setEmailError(emailRegex.test(value) ? "" : "Invalid email format");
//       }, 600);
//       return;
//     }

//     // CONTACT VALIDATION
//     if (name === "contactno") {
//       if (/^\d{0,10}$/.test(value)) {
//         setFormData((prev) => ({ ...prev, contactno: value }));
//         setContactError(
//           value.length === 10 ? "" : "Contact number must be exactly 10 digits"
//         );
//       }
//       return;
//     }

//     if (name === "adharNo") {
//       if (/^\d{0,12}$/.test(value)) {
//         setFormData((p) => ({ ...p, adharNo: value }));
//       }
//       return;
//     }


//     // Designation
//     if (name === "designation") {
//       setFormData((prev) => ({ ...prev, designation: value }));
//       setShowOtherDesignation(value === "Other");
//       if (value !== "Other") setOtherDesignation("");
//       return;
//     }

//     // NORMAL FIELDS
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   /* ---------------- IMAGE SELECT ---------------- */
//   const handleImageSelect = (e) => {
//     setImageFile(e.target.files[0]);
//   };

//   /* ---------------- IMAGE UPLOAD ---------------- */
//   const uploadImage = async (vipId) => {
//     if (!imageFile) return;

//     const token = localStorage.getItem("adminToken");
//     const formData = new FormData();
//     formData.append("imaged", imageFile);

//     await axios.post(
//       `${BASE_URL}/api/profile/upload/${vipId}/vip`,
//       formData,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "multipart/form-data",
//         },
//       }
//     );
//   };


//   /* -------------------------------------------------------
//      SUBMIT
//   ------------------------------------------------------- */
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (emailError || contactError) {
//       toast.error("Please fix all errors!");
//       return;
//     }

//     if (!imageFile) {
//       toast.error("Please select profile picture!");
//       return;
//     }

//     const finalData = {
//       ...formData,
//       designation:
//         formData.designation === "Other"
//           ? otherDesignation
//           : formData.designation,

//       status: "Inactive",
//     };

//     try {
//       setLoading(true);

//       if (isEditMode) {
//         const res = await updateVip(formData.id, finalData);
//         toast.success(res?.message || "VIP details updated successfully!");
//       } else {
//         const res = await submit_vip_FormData(finalData);
//         toast.success(res?.message || "VIP registered successfully!");
//         await uploadImage(res.id);
//       }

//       /* 2️⃣ AUTO UPLOAD IMAGE */

//       toast.success("Vip registered with profile picture!");

//       setRefers("true");
//       resetForm();
//     } catch (err) {
//       const errorMsg =
//         err?.response?.data?.message ||
//         err?.response?.data?.error ||
//         err?.message ||
//         "Failed to save data";

//       toast.error(errorMsg);
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* -------------------------------------------------------
//      RESET
//   ------------------------------------------------------- */
//   const resetForm = () => {
//     setFormData({
//       id: "",
//       name: "",
//       email: "",
//       gender: "",
//       adharNo: "",
//       username: "",
//       password: "",
//       designation: "",
//       contactno: "",
//       picture: "",
//       status: "Inactive",
//     });
//     setOtherDesignation("");
//     setShowOtherDesignation(false);
//     setEmailError("");
//     setContactError("");
//     setIsEditMode(false);
//     setImageFile(null);
//   };

//   /* -------------------------------------------------------
//      UI
//   ------------------------------------------------------- */
//   return (
//     <>
//       <style>{CSS}</style>

//       <div className="container">
//         <div className="registration-card">
//           {/* HEADER */}
//           <div className="header-section">
//             <div className="header-content">
//               <h1 className="header-title">
//                 {isEditMode ? "Edit VIP Details" : "VIP Registration Form"}
//               </h1>
//               <p className="header-description">
//                 Register VIP visitors with complete personal details.
//               </p>
//             </div>

//             <div className={`mode-display ${isEditMode ? "edit-mode" : ""}`}>
//               {isEditMode ? "Edit Mode" : "Registration Mode"}
//             </div>
//           </div>

//           {/* FORM */}
//           <div className="form-section">
//             <form onSubmit={handleSubmit}>
//               <div className="form-layout">
//                 <div className="form-field">
//                   <label className="field-label">Full Name *</label>
//                   <input
//                     name="name"
//                     value={formData.name}
//                     onChange={handleChange}
//                     required
//                   />
//                 </div>

//                 <div className="form-field">
//                   <label className="field-label">Email *</label>
//                   <input
//                     name="email"
//                     value={formData.email}
//                     onChange={handleChange}
//                     required
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label className="field-label">Aadhaar Number *</label>
//                   <input
//                     name="adharNo"
//                     value={formData.adharNo}
//                     onChange={handleChange}
//                     required
//                   />
//                 </div>
//                 <div className="form-field full-width-field">
//                   <label className="field-label">Gender *</label>
//                   <select name="gender" value={formData.gender} onChange={handleChange} required>
//                     <option value="">Select Gender</option>
//                     <option>Male</option>
//                     <option>Female</option>
//                     <option>Other</option>
//                   </select>
//                 </div>


//                 <div className="form-field">
//                   <label className="field-label">Username *</label>
//                   <input
//                     name="username"
//                     value={formData.username}
//                     onChange={handleChange}
//                     required
//                   />
//                 </div>

//                 <div className="form-field">
//                   <label className="field-label">Password *</label>
//                   <input
//                     type="text"
//                     name="password"
//                     value={formData.password}
//                     onChange={handleChange}
//                     required
//                   />
//                 </div>

//                 {/* DESIGNATION */}
//                 <div className="form-field full-width-field">
//                   <label className="field-label">Designation *</label>
//                   <select
//                     name="designation"
//                     value={formData.designation}
//                     onChange={handleChange}
//                     required
//                   >
//                     <option value="">Select Designation</option>

//                     {designations.map((d, i) => (
//                       <option key={i} value={d}>
//                         {d}
//                       </option>
//                     ))}

//                     <option value="Other">Other</option>
//                   </select>
//                 </div>

//                 {/* OTHER DESIGNATION */}
//                 {showOtherDesignation && (
//                   <div className="other-rank-wrapper visible">
//                     <input className="w-100"
//                       placeholder="Enter custom designation"
//                       value={otherDesignation}
//                       onChange={(e) =>
//                         setOtherDesignation(e.target.value)
//                       }
//                       required
//                     />
//                   </div>
//                 )}

//                 <div className="form-field">
//                   <label className="field-label">Contact *</label>
//                   <input
//                     name="contactno"
//                     value={formData.contactno}
//                     onChange={handleChange}
//                     required
//                   />
//                 </div>
//               </div>
//               <div className="form-field full-width-field">
//                 <label className="field-label">Profile Picture *</label>
//                 <input type="file" accept="image/*" onChange={handleImageSelect} />
//               </div>
//               <div className="actions-section">
//                 <button
//                   className="submit-btn"
//                   type="submit"
//                   disabled={loading}
//                 >
//                   {loading
//                     ? "Processing..."
//                     : isEditMode
//                       ? "Update VIP"
//                       : "Submit VIP"}
//                 </button>

//                 {isEditMode && (
//                   <button
//                     type="button"
//                     className="cancel-btn"
//                     onClick={resetForm}
//                   >
//                     Cancel Update
//                   </button>
//                 )}
//               </div>
//             </form>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// /* ------------------ SAME UI CSS ------------------ */
// const CSS = `
// * { margin:0; padding:0; box-sizing:border-box; }
// .container { max-width:1100px; margin:auto; padding:20px;}
// .registration-card {
//   background: linear-gradient(135deg, rgba(190,235,120,0.45), rgba(120,200,255,0.45));
//   border-radius:20px;
//   box-shadow:0 15px 40px rgba(0,0,0,.1);
//   overflow:hidden;
// }
// .header-section {
//   background:linear-gradient(135deg,#1e3a8a,#3b82f6);
//   padding:35px 40px 45px;
//   color:#fff;
//   position:relative;
// }
// .header-title { font-size:32px; font-weight:700; color:#fff; }
// .mode-display {
//   position:absolute;
//   right:40px;
//   bottom:-18px;
//   background:#f59e0b;
//   padding:10px 22px;
//   border-radius:50px;
//   color:white;
//   font-weight:600;
// }
// .mode-display.edit-mode { background:#059669; }
// .form-section { padding:50px 40px; }
// .form-layout {
//   display:grid;
//   grid-template-columns:repeat(2,1fr);
//   gap:28px;
// }
// .form-field { display:flex; flex-direction:column; }
// .full-width-field { grid-column:span 2; }
// .other-rank-wrapper { grid-column:span 2; }
// input, select {
//   padding:15px 20px;
//   border:2px solid #e5e7eb;
//   border-radius:12px;
// }
// .actions-section {
//   margin-top:40px;
//   display:flex;
//   gap:12px;
// }
// .submit-btn {
//   flex:1;
//   background:#2563eb;
//   color:white;
//   padding:18px;
//   border:none;
//   border-radius:12px;
// }
// .cancel-btn {
//   background:#f3f4f6;
//   padding:18px;
//   border-radius:12px;
//   border:2px solid #ddd;
// }
// `;
